import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams as useRouterSearchParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search, Star, Globe, Mail, Phone, MapPin,
  Facebook, Instagram, Linkedin, Twitter, Youtube,
  Sparkles, Save, ExternalLink, Loader2, Info, ChevronDown,
  Filter, SlidersHorizontal, UserCheck, Users as UsersIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LeadSearch = () => {
  const [urlParams] = useRouterSearchParams();
  const preselectedNiche = urlParams.get('niche') || '';
  
  const [searchParams, setSearchParams] = useState({
    niche: preselectedNiche,
    state: '',
    city: ''
  });
  
  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({
    minRating: '',
    minReviews: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  const [leads, setLeads] = useState([]);
  const [states, setStates] = useState([]);
  const [niches, setNiches] = useState([]);
  const [nichesLabel, setNichesLabel] = useState('');
  const [nichesHelper, setNichesHelper] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [savingLead, setSavingLead] = useState(null);
  const [enrichment, setEnrichment] = useState(null);
  const [enriching, setEnriching] = useState(false);
  
  // Pagination state
  const [nextPageToken, setNextPageToken] = useState(null);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [dataSource, setDataSource] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [searchContext, setSearchContext] = useState(null); // For Google Places pagination

  useEffect(() => {
    fetchOptions();
  }, []);
  
  // Update search params when URL niche parameter changes
  useEffect(() => {
    if (preselectedNiche && preselectedNiche !== searchParams.niche) {
      setSearchParams(prev => ({ ...prev, niche: preselectedNiche }));
    }
  }, [preselectedNiche, searchParams.niche]);

  const fetchOptions = async () => {
    try {
      const [statesRes, nichesRes] = await Promise.all([
        axios.get(`${API_URL}/leads/states`),
        axios.get(`${API_URL}/leads/niches`)
      ]);
      setStates(statesRes.data.states || []);
      setNiches(nichesRes.data.niches || []);
      setNichesLabel(nichesRes.data.label || 'Niches');
      setNichesHelper(nichesRes.data.helper_text || '');
    } catch (error) {
      console.error('Failed to fetch options:', error);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!searchParams.niche) {
      toast.error('Please select an AI-optimized niche');
      return;
    }
    
    setLoading(true);
    setSelectedLead(null);
    setAnalysis(null);
    // Reset pagination for new search
    setLeads([]);
    setNextPageToken(null);
    setTotalLoaded(0);
    setDataSource(null);
    setSearchContext(null);

    try {
      const params = new URLSearchParams();
      params.append('niche', searchParams.niche);
      if (searchParams.state) params.append('state', searchParams.state);
      if (searchParams.city) params.append('city', searchParams.city);
      params.append('limit', '20');

      const response = await axios.get(`${API_URL}/leads/search?${params}`);
      const newLeads = response.data.leads || [];
      
      setLeads(newLeads);
      setNextPageToken(response.data.next_page_token || null);
      setTotalLoaded(newLeads.length);
      setDataSource(response.data.data_source);
      setSearchContext(response.data.search_context || null);
      setLastSearchParams({ ...searchParams });
      
      if (newLeads.length === 0) {
        toast.info('No leads found for this search');
      }
    } catch (error) {
      toast.error('Failed to search leads');
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = useCallback(async () => {
    if (!nextPageToken || loadingMore) return;
    
    setLoadingMore(true);
    
    // Google Places API requires a short delay before using next_page_token
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      const params = new URLSearchParams();
      if (lastSearchParams?.niche) params.append('niche', lastSearchParams.niche);
      if (lastSearchParams?.state) params.append('state', lastSearchParams.state);
      if (lastSearchParams?.city) params.append('city', lastSearchParams.city);
      params.append('limit', '20');
      params.append('page_token', nextPageToken);
      // Include search_context for Google Places pagination to work correctly
      if (searchContext) params.append('search_context', searchContext);

      const response = await axios.get(`${API_URL}/leads/search?${params}`);
      const newLeads = response.data.leads || [];
      
      // Append new results to existing leads
      setLeads(prevLeads => [...prevLeads, ...newLeads]);
      setNextPageToken(response.data.next_page_token || null);
      setTotalLoaded(prev => prev + newLeads.length);
      // Update search_context if returned (for subsequent pagination)
      if (response.data.search_context) {
        setSearchContext(response.data.search_context);
      }
      
      if (newLeads.length === 0) {
        toast.info('No more leads available');
      } else {
        toast.success(`Loaded ${newLeads.length} more leads`);
      }
    } catch (error) {
      toast.error('Failed to load more leads');
      console.error('Load more error:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [nextPageToken, loadingMore, lastSearchParams, searchContext]);

  const handleAnalyze = async (lead) => {
    setSelectedLead(lead);
    setAnalyzing(true);
    setAnalysis(null);
    setEnrichment(null);

    // Bring the analysis panel into view immediately so the user can see the loading state
    // even when they clicked a lead far down the list. Works on mobile (panel renders below)
    // and on desktop (sticky pin re-anchors visibly).
    setTimeout(() => {
      const el = document.getElementById('analysis-panel');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);

    try {
      const response = await axios.post(`${API_URL}/ai/analyze-preview`, lead);
      setAnalysis(response.data.analysis);
    } catch (error) {
      toast.error('Failed to analyze business');
      console.error('Analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEnrich = async () => {
    if (!selectedLead?.website) {
      toast.error('This business has no website to enrich.');
      return;
    }
    setEnriching(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/leads/enrich-preview`,
        { website: selectedLead.website, business_name: selectedLead.business_name },
        { timeout: 90000 }
      );
      setEnrichment(data.enrichment);
      const dm = data.enrichment?.decision_makers?.length || 0;
      const sm = Object.keys(data.enrichment?.socials || {}).length;
      const em = data.enrichment?.emails?.length || 0;
      if (dm + sm + em > 0) {
        toast.success(`Found ${dm} decision-maker(s), ${em} email(s), ${sm} social profile(s)`);
      } else if (data.enrichment?.status === 'unreachable') {
        toast.error("Couldn't reach the website.");
      } else {
        toast.message("No public contact data found on the website.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enrichment failed');
    } finally {
      setEnriching(false);
    }
  };

  const handleSaveLead = async (lead) => {
    setSavingLead(lead.id);
    try {
      await axios.post(`${API_URL}/leads/save`, lead);
      toast.success('Lead saved to your CRM!');
    } catch (error) {
      toast.error('Failed to save lead');
      console.error('Save error:', error);
    } finally {
      setSavingLead(null);
    }
  };

  const getSocialIcon = (platform) => {
    const icons = {
      facebook: Facebook,
      instagram: Instagram,
      linkedin: Linkedin,
      twitter: Twitter,
      youtube: Youtube,
    };
    return icons[platform] || Globe;
  };

  return (
    <DashboardLayout>
      <Header 
        title="Find Leads" 
        subtitle="Search for business leads in AI-optimized niches across the US"
      />
      
      <div className="p-8" data-testid="search-content">
        {/* Search Form */}
        <div className="card-dashboard mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Niche Selection with Label */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label htmlFor="niche" className="text-base font-semibold">
                  {nichesLabel || 'AI-Optimized Niches'} *
                </Label>
                <div className="group relative">
                  <Info className="w-4 h-4 text-slate-400 cursor-help" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-slate-900 text-white text-xs rounded-lg shadow-lg z-10">
                    {nichesHelper || 'These niches are optimized for the best AI insights and outreach results.'}
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-2">
                {nichesHelper || 'These niches are optimized for the best AI insights and outreach results.'}
              </p>
              <Select
                value={searchParams.niche}
                onValueChange={(value) => setSearchParams({ ...searchParams, niche: value })}
              >
                <SelectTrigger className="w-full" data-testid="niche-select">
                  <SelectValue placeholder="Select an AI-optimized niche" />
                </SelectTrigger>
                <SelectContent>
                  {niches.map((niche) => (
                    <SelectItem key={niche.key} value={niche.key}>
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-indigo-500" />
                        <span>{niche.display_name}</span>
                        <span className="text-slate-400 text-xs">({niche.category})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="state">State</Label>
                <Select
                  value={searchParams.state}
                  onValueChange={(value) => setSearchParams({ ...searchParams, state: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="mt-1" data-testid="state-select">
                    <SelectValue placeholder="All States" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {states.map((state) => (
                      <SelectItem key={state.abbreviation} value={state.name}>
                        {state.name} ({state.abbreviation})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="city">City (Optional)</Label>
                <Input
                  id="city"
                  value={searchParams.city}
                  onChange={(e) => setSearchParams({ ...searchParams, city: e.target.value })}
                  placeholder="Enter city"
                  className="mt-1"
                  data-testid="city-input"
                />
              </div>

              <div className="flex items-end">
                <Button type="submit" className="w-full btn-primary" disabled={loading || !searchParams.niche} data-testid="search-btn">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Search Leads
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Advanced Filters
                <ChevronDown className={`w-4 h-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </button>

              {showAdvancedFilters && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label htmlFor="minRating">Min Rating</Label>
                    <Select
                      value={advancedFilters.minRating || 'any'}
                      onValueChange={(value) => setAdvancedFilters({ ...advancedFilters, minRating: value === 'any' ? '' : value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="min-rating-select">
                        <SelectValue placeholder="Any Rating" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Rating</SelectItem>
                        <SelectItem value="3">3+ Stars</SelectItem>
                        <SelectItem value="3.5">3.5+ Stars</SelectItem>
                        <SelectItem value="4">4+ Stars</SelectItem>
                        <SelectItem value="4.5">4.5+ Stars</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="minReviews">Min Reviews</Label>
                    <Select
                      value={advancedFilters.minReviews || 'any'}
                      onValueChange={(value) => setAdvancedFilters({ ...advancedFilters, minReviews: value === 'any' ? '' : value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="min-reviews-select">
                        <SelectValue placeholder="Any Count" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Count</SelectItem>
                        <SelectItem value="10">10+ Reviews</SelectItem>
                        <SelectItem value="25">25+ Reviews</SelectItem>
                        <SelectItem value="50">50+ Reviews</SelectItem>
                        <SelectItem value="100">100+ Reviews</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2 flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAdvancedFilters({ minRating: '', minReviews: '' })}
                      className="text-sm"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lead List */}
          <div className="lg:col-span-2 space-y-4">
            {(() => {
              // Apply advanced filters to leads
              const filteredLeads = leads.filter(lead => {
                if (advancedFilters.minRating && lead.google_rating) {
                  if (lead.google_rating < parseFloat(advancedFilters.minRating)) return false;
                }
                if (advancedFilters.minReviews && lead.review_count !== undefined) {
                  if (lead.review_count < parseInt(advancedFilters.minReviews)) return false;
                }
                return true;
              });
              
              const hasActiveFilters = advancedFilters.minRating || advancedFilters.minReviews;
              
              return filteredLeads.length > 0 ? (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-sm text-slate-500">
                      Showing {hasActiveFilters ? `${filteredLeads.length} of ${totalLoaded}` : totalLoaded} businesses
                      {nextPageToken && <span className="text-violet-600 ml-1">(more available)</span>}
                    </p>
                    {hasActiveFilters && (
                      <span className="badge-info flex items-center gap-1">
                        <Filter className="w-3 h-3" />
                        Filtered
                      </span>
                    )}
                  </div>
                  <span className="badge-gradient flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI-Optimized Results
                  </span>
                </div>
                {filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className={`card-dashboard cursor-pointer transition-all ${
                      selectedLead?.id === lead.id ? 'ring-2 ring-indigo-500' : ''
                    }`}
                    onClick={() => handleAnalyze(lead)}
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{lead.business_name}</h3>
                        <p className="text-sm text-indigo-600">{lead.niche}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium text-slate-700">{lead.google_rating}</span>
                        <span className="text-slate-400 text-sm">({lead.review_count})</span>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {lead.city}, {lead.state}
                      </div>
                      {lead.phone && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {lead.phone}
                        </div>
                      )}
                      {lead.email && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {lead.email}
                        </div>
                      )}
                      {lead.website && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Globe className="w-4 h-4 text-slate-400" />
                          <a 
                            href={lead.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:text-indigo-600 truncate"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Website
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Social Links */}
                    {lead.social_links && Object.entries(lead.social_links).some(([_, v]) => v) && (
                      <div className="mt-3 flex items-center gap-2">
                        {Object.entries(lead.social_links).map(([platform, url]) => {
                          if (!url) return null;
                          const Icon = getSocialIcon(platform);
                          return (
                            <a
                              key={platform}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Icon className="w-4 h-4" />
                            </a>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-4 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAnalyze(lead);
                        }}
                        data-testid={`analyze-btn-${lead.id}`}
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        AI Analysis
                      </Button>
                      <Button
                        size="sm"
                        className="btn-accent"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveLead(lead);
                        }}
                        disabled={savingLead === lead.id}
                        data-testid={`save-btn-${lead.id}`}
                      >
                        {savingLead === lead.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-1" />
                            Save Lead
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Load More Button */}
                {nextPageToken && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="btn-primary px-8"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading more leads...
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-2" />
                          Load More Leads
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* End of Results */}
                {!nextPageToken && leads.length > 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-500">
                      All available businesses loaded ({totalLoaded} total)
                    </p>
                  </div>
                )}
              </>
            ) : leads.length > 0 ? (
              <div className="card-dashboard text-center py-16">
                <Filter className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Matching Leads</h3>
                <p className="text-slate-500 mb-4">
                  No leads match your current filters. Try adjusting the rating or review requirements.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setAdvancedFilters({ minRating: '', minReviews: '' })}
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="card-dashboard text-center py-16">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Search for Leads</h3>
                <p className="text-slate-500 mb-4">
                  Select an AI-optimized niche to find high-quality business leads
                </p>
                <p className="text-sm text-indigo-600">
                  Our platform is optimized for specific high-value US business niches to ensure better lead quality and higher response rates.
                </p>
              </div>
            );
            })()}
          </div>

          {/* AI Analysis Panel */}
          <div className="lg:col-span-1">
            <div
              id="analysis-panel"
              className="card-base p-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto scroll-mt-24"
            >
              <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                Niche-Specific AI Analysis
              </h3>

              {analyzing ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
                  <p className="text-slate-500">Analyzing business...</p>
                  <p className="text-xs text-slate-400 mt-1">Using industry-specific AI insights</p>
                </div>
              ) : analysis ? (
                <div className="space-y-4">
                  {/* Score */}
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-600 font-medium mb-1">Online Presence Score</p>
                    <div className="flex items-center gap-3">
                      <div className="text-3xl font-bold text-indigo-700">{analysis.online_presence_score}</div>
                      <div className="flex-1 h-2 bg-indigo-100 rounded-full">
                        <div 
                          className="h-full bg-indigo-600 rounded-full transition-all"
                          style={{ width: `${analysis.online_presence_score}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Pain Points */}
                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-2">Industry Pain Points</p>
                    <ul className="space-y-2">
                      {analysis.pain_points?.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-1.5 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Recommendations */}
                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-2">Niche-Specific Services</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.service_recommendations?.map((service, i) => (
                        <span key={i} className="badge-success">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Priority Action */}
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm font-medium text-emerald-700 mb-1">Priority Action</p>
                    <p className="text-sm text-emerald-600">{analysis.priority_action}</p>
                  </div>

                  {/* Summary */}
                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-2">Analysis Summary</p>
                    <p className="text-sm text-slate-600">{analysis.analysis_summary}</p>
                  </div>

                  {/* ============ FREE LEAD ENRICHMENT ============ */}
                  <div className="pt-4 mt-2 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                          <UserCheck className="w-4 h-4 text-violet-600" />
                          Decision Makers & Contacts
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Pulls owner names, emails, and social profiles from the business' own website.
                        </p>
                      </div>
                    </div>

                    {!enrichment && !enriching && (
                      <button
                        onClick={handleEnrich}
                        disabled={!selectedLead?.website}
                        data-testid="enrich-btn"
                        className="btn-primary w-full"
                      >
                        <Sparkles className="w-4 h-4" />
                        {selectedLead?.website ? 'Find decision makers' : 'No website on file'}
                      </button>
                    )}

                    {enriching && (
                      <div className="flex items-center justify-center py-6 text-sm text-slate-500 gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
                        Scanning website…
                      </div>
                    )}

                    {enrichment && enrichment.status === 'unreachable' && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        Couldn't reach this website. It may block automated visitors.
                      </p>
                    )}
                    {enrichment && enrichment.status === 'no_data' && (
                      <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                        No public contact info found on the website. Try the email/phone listed in the lead card.
                      </p>
                    )}

                    {enrichment && enrichment.status === 'ok' && (
                      <div className="space-y-3">
                        {/* Decision Makers */}
                        {enrichment.decision_makers?.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] mb-2">People</p>
                            <ul className="space-y-1.5">
                              {enrichment.decision_makers.map((p, i) => (
                                <li key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-violet-50/50 border border-violet-100" data-testid={`enrich-person-${i}`}>
                                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                    {(p.name || '?').slice(0, 1).toUpperCase()}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                                    <p className="text-xs text-slate-600 truncate">{p.title}</p>
                                  </div>
                                  {p.confidence && (
                                    <span className={
                                      p.confidence === 'high' ? 'badge-success' :
                                      p.confidence === 'medium' ? 'badge-warning' : 'badge-neutral'
                                    }>
                                      {p.confidence}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Emails */}
                        {enrichment.emails?.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] mb-2">Emails found</p>
                            <ul className="space-y-1">
                              {enrichment.emails.slice(0, 8).map((e, i) => (
                                <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" data-testid={`enrich-email-${i}`}>
                                  <a href={`mailto:${e.email}`} className="font-mono text-slate-800 hover:text-violet-700 truncate">
                                    {e.email}
                                  </a>
                                  <span className={e.kind === 'personal' ? 'badge-info' : 'badge-neutral'}>
                                    {e.kind}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Socials */}
                        {Object.keys(enrichment.socials || {}).length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase font-bold text-slate-500 tracking-[0.14em] mb-2">Social profiles</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(enrichment.socials).map(([platform, url]) => {
                                const Icon = getSocialIcon(platform);
                                return (
                                  <a
                                    key={platform}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`enrich-social-${platform}`}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:border-violet-300 hover:text-violet-700 transition-colors"
                                  >
                                    <Icon className="w-3.5 h-3.5" /> {platform}
                                    <ExternalLink className="w-3 h-3 opacity-50" />
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {enrichment.pages_scraped?.length > 0 && (
                          <p className="text-[10px] text-slate-400 mt-1">
                            Sourced from {enrichment.pages_scraped.length} page(s) on the business website.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500 text-sm">
                    Click on a lead to see niche-specific AI analysis and service recommendations
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LeadSearch;
